import { useMutation, useQueryClient } from '@tanstack/react-query';

import { markNotificationsSeen, notificationQueries } from '../api/notification';
import useNotificationApiInstance from './useNotificationApiInstance';

import type { AxiosError } from 'axios';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { NotificationBadge } from '../constants';

type UseMarkNotificationsSeenOptions = UseMutationOptions<void, AxiosError, void>;

/**
 * GNB bell 클릭 시 사용자의 lastSeenAt cursor 갱신.
 * @description
 * - 성공하면 `notificationQueries.badge` 를 invalidate 해서 unseenCount 를 최신화.
 */
const useMarkNotificationsSeen = (options?: UseMarkNotificationsSeenOptions) => {
  const queryClient = useQueryClient();
  const { instance } = useNotificationApiInstance();

  return useMutation({
    mutationFn: () => markNotificationsSeen(instance),
    ...options,
    onMutate: async variables => {
      const queryKey = notificationQueries.badge(instance).queryKey;

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<NotificationBadge>(queryKey, old =>
        old ? { ...old, unseenCount: 0 } : old,
      );

      await options?.onMutate?.(variables);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.badge(instance).queryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export default useMarkNotificationsSeen;
